/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-shell
 * @responsibility Declare the filter scopes (`FKEY`) and per-scope filter definitions (`FDEF`).
 * @state-owner navigation store holds the selected values
 * @allowed-imports shared view types
 * @forbidden React; network; store implementation
 * @public-api workspace shell and modules
 * @side-effects none
 * @tests workspace-behaviour.test.ts
 */

import type { ClassRecord, WorkspaceRole, WorkspaceSection } from './model';

export interface FilterDefinition {
  readonly k: string;
  readonly t: string;
  readonly o: readonly (readonly [string, string])[];
}

export function filterScope(role: WorkspaceRole, section: WorkspaceSection): string {
  if (role === 'teacher') return section;
  if (section === 'act') return 'sact';
  if (section === 'room') return 'sroom';
  return 'srep';
}

export function filterDefinitions(
  scope: string,
  classes: readonly ClassRecord[],
  myClassIndexes: readonly number[],
): readonly FilterDefinition[] {
  const classOptions = classes.map((item, index) => [String(index), item.name] as const);
  switch (scope) {
    case 'act':
      return [
        {
          k: 'st',
          t: '狀態',
          o: [
            ['parsing', '解析中'],
            ['review', '待確認'],
            ['ready', '可用'],
          ],
        },
        { k: 'ci', t: '班級', o: classOptions },
        {
          k: 'ans',
          t: '答案來源',
          o: [
            ['file', '有答案檔'],
            ['ai', 'AI 生成'],
          ],
        },
      ];
    case 'room':
      return [
        {
          k: 'f',
          t: '狀態',
          o: [
            ['live', '上課中'],
            ['need', '有人需要你'],
            ['rev', '有待審報告'],
          ],
        },
      ];
    case 'live':
      return [
        {
          k: 'f',
          t: '狀態',
          o: [
            ['need', '需要你'],
            ['ok', '順利'],
          ],
        },
        { k: 'ci', t: '班級', o: classOptions },
      ];
    case 'rep':
      return [
        { k: 'f', t: '狀態', o: [['need', '需要注意']] },
        { k: 'ci', t: '班級', o: classOptions },
      ];
    case 'sact':
      return [
        {
          k: 'f',
          t: '狀態',
          o: [
            ['live', '進行中'],
            ['open', '還沒交'],
            ['fix', '要訂正'],
            ['done', '已完成'],
          ],
        },
        {
          k: 'ci',
          t: '班級',
          o: myClassIndexes.map((index) => [String(index), classes[index].name] as const),
        },
      ];
    default:
      return [];
  }
}

/** The display defaults every scope starts from. */
export function defaultDisplay(scope: string) {
  return {
    view: (scope === 'act' || scope === 'sact' ? 'board' : 'list') as 'list' | 'board',
    grp: 'st' as const,
    ord: 'upd' as const,
    props: ['q', 'cls', 'upd'] as readonly string[],
  };
}

export const DISPLAY_PROPERTY_OPTIONS: readonly (readonly [string, string])[] = [
  ['q', '題數'],
  ['cls', '班級'],
  ['ans', '答案來源'],
  ['upd', '更新時間'],
];
